import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { SkipLink } from '../a11y/SkipLink';
import { useAuth } from '../../providers/AuthProvider';

const NAV: { to: string; label: string; end: boolean; ownerOnly?: boolean }[] = [
  { to: '/admin', label: 'Dashboard', end: true },
  { to: '/admin/properties', label: 'Properties', end: false },
  { to: '/admin/tenants', label: 'Tenants', end: false },
  { to: '/admin/leases', label: 'Leases', end: false },
  { to: '/admin/work-orders', label: 'Work orders', end: false },
  { to: '/admin/contractors', label: 'Contractors', end: false },
  { to: '/admin/documents', label: 'Documents', end: false },
  { to: '/admin/messages', label: 'Messages', end: false },
  { to: '/admin/payment-methods', label: 'Payment methods', end: false },
  { to: '/admin/staff', label: 'Staff', end: false, ownerOnly: true },
  { to: '/admin/audit', label: 'Audit log', end: false, ownerOnly: true },
  { to: '/admin/account', label: 'Account', end: false },
];

/** Best-effort page title for the current path, from NAV (longest/most-specific match wins). */
function pageTitle(pathname: string): string {
  const hit = NAV.filter((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to))).sort(
    (a, b) => b.to.length - a.to.length,
  )[0];
  return hit?.label ?? 'Admin';
}

/** The owner/staff admin shell: semantic header/nav/main landmarks + a skip link + logout. */
export function AdminLayout() {
  const { principal, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  // SPA route change (WCAG 2.4.3 Focus Order): move keyboard focus onto <main> — off the stale
  // nav link — and announce the new page via the visually-hidden aria-live region below, so
  // screen-reader users get the same "page changed" signal a full navigation would give them.
  useEffect(() => {
    mainRef.current?.focus();
  }, [location.pathname]);

  return (
    <>
      <SkipLink />
      <header className="app-header">
        <p className="brand">Garage Band LLC — Admin</p>
        <nav className="app-nav" aria-label="Admin">
          <ul>
            {NAV.filter((n) => !n.ownerOnly || principal?.role === 'owner').map((n) => (
              <li key={n.to}>
                <NavLink to={n.to} end={n.end}>
                  {n.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <span className="header-spacer" />
        <div className="row">
          {principal && (
            <span className="muted">
              {principal.displayName} ({principal.role})
            </span>
          )}
          <button
            type="button"
            className="secondary"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
          >
            Sign out
          </button>
        </div>
      </header>
      <main id="main-content" tabIndex={-1} ref={mainRef}>
        <Outlet />
      </main>
      <div className="visually-hidden" role="status" aria-live="polite">
        {pageTitle(location.pathname)} page loaded
      </div>
    </>
  );
}
